import { range } from "lodash";
import { MobergAnimationTimingMs } from "../../../../../../../../Moberg";
import { D3ClipPathConfig } from "../../../D3/D3ClipPath";
import { D3HorizontalLinesConfig } from "../../../D3/D3HorizontalLines";
import { D3ParabolaConfig } from "../../../D3/D3Parabola";
import { D3PointConfig } from "../../../D3/D3Point";
import { D3ValueAxisConfig } from "../../../D3/D3ValueAxis";
import { D3TimelineConfig } from "../../../D3/Timeline/D3Timeline";
import { D3ErrorBarsConfig } from "../../CPPOpt/D3/D3CPPOptErrorBars";
import { ConfigurationBuilder } from "../../D3ConfigurationBuilder"
import { HistogramXAxisConfig } from "../../Histogram/D3/D3HistogramXAxis";
import { D3MAPOptPlot } from "./D3MAPOptPlot";
import { MAPOptData } from "../../../../Types/MAPOptPlot";

export class D3MAPOptPlotConfigurationBuilder extends ConfigurationBuilder<D3MAPOptPlot> {
	private bins = range(21, 121, 2)
	public transitionDuration: number = MobergAnimationTimingMs.SLOW
	public mapOptData: MAPOptData = {
		MAPOpt: null,
		LLA: null,
		ULA: null,
		includedDataPercentage: null,
		fitType: null,
		fitSpan: null,
		yValues: new Array(this.bins.length).fill(null),
		errors: new Array(this.bins.length).fill(null),
		includedInFit: new Array(this.bins.length).fill(null),
		dataPercentages: new Array(this.bins.length).fill(null),
		coefficients: [null, null, null],
		weight: null
	}

    getClipPathConfig = (): D3ClipPathConfig => {
		return {
			id: this.getClipPathId(),
			boundingBox: this.visualization.boundingBox
		}
	}

    getTimelineConfig = (): D3TimelineConfig => {
		const timelineUpdateTimes = (start: number, end: number) => {
			this.visualization.viewTimesChanged(start, end)
			this.visualization.updateLinkedWindows()
		}

		return {
			id: this.visualization.config.id,
			viewScale: this.visualization.config.viewScale,
			width: this.visualization.boundingBox.width,
			annotations: [],
			fileScale: this.visualization.config.fileScale,
			playbackSpeed: this.visualization.config.playbackSpeed,
			currentTimelineController: this.visualization.config.timelineController,
			isLinked: this.visualization.config.isLinked,
			timeZone: this.visualization.config.timeZone,
			liveModeEnabled: this.visualization.config.liveModeEnabled,
			onDrag: this.visualization.onTimelineSliderDrag,
			onDragEnd: this.visualization.onTimelineSliderDragEnd,
			updateViewTimes: timelineUpdateTimes,
			goToStart: this.visualization.goToStart,
			goToEnd: this.visualization.goToEnd,
			goToNextPage: this.visualization.goToNextPage,
			goToPreviousPage: this.visualization.goToPreviousPage,
		}
	}

    getXAxisConfig = (): HistogramXAxisConfig => ({
        scale: this.visualization.xScaleBand,
        boundingBox: this.visualization.boundingBox,
		label: "MAP (mmHg)",
    })

	getYAxisConfig = (): D3ValueAxisConfig => ({
		graphId: this.visualization.config.id,
		scale: this.visualization.yScale,
		label: "COx",
		onDrag: this.visualization.onYAxisDrag,
		onDragEnd: this.visualization.onYAxisDragEnd,
		onDragStart: this.visualization.onYAxisDragStart,
		onDoubleClick: this.visualization.autoScale
	})

	getHorizontalLinesConfig = (): D3HorizontalLinesConfig => ({
		yScale: this.visualization.yScale,
		width: this.visualization.boundingBox.width,
	})

	getParabolaConfig = (): D3ParabolaConfig => ({
		xScale: this.visualization.xScaleLinear,
		yScale: this.visualization.yScale,
		coefficients: this.mapOptData.coefficients,
		clipPathId: this.getClipPathId(),
		color: "black",
		isDashed: true,
		transitionDuration: this.transitionDuration,
		applyInverseFisher: true
	})

	getErrorBarsConfig = (): D3ErrorBarsConfig => ({
		yScale: this.visualization.yScale,
		xScale: this.visualization.xScaleLinear,
		clipPathId: this.getClipPathId(),
		bars: this.bins.map((x, index) => ({
			x,
			y: this.mapOptData.yValues[index],
			error: this.mapOptData.errors[index],
			includedInFit: this.mapOptData.includedInFit[index] === 1,
			dataPercentage: this.mapOptData.dataPercentages[index],
		})).filter(bar => bar.y != null),
		transitionDuration: this.transitionDuration,
	})

	getMinimumPointConfig = (x: number, y: number): D3PointConfig => ({
		x,
		y,
		xScale: this.visualization.xScaleLinear,
		yScale: this.visualization.yScale,
		color: "blue",
		size: 5,
		clipPathId: this.getClipPathId(),
		transitionDuration: this.transitionDuration,
	})

	updateMAPOptData = (data: MAPOptData) => {
		this.mapOptData = data
	} 

	getLLAPointConfig = (x: number, y: number): D3PointConfig => ({
		color: "purple",
		size: 5,
		x,
		y,
		xScale: this.visualization.xScaleLinear,
		yScale: this.visualization.yScale,
		clipPathId: this.getClipPathId(),
		transitionDuration: this.transitionDuration,
	})

	getULAPointConfig = (x: number, y: number): D3PointConfig => ({
		color: "purple",
		size: 5,
		x,
		y,
		xScale: this.visualization.xScaleLinear,
		yScale: this.visualization.yScale,
		clipPathId: this.getClipPathId()
	})

    private getClipPathId = () => `d3-clip-path-${this.visualization.config.id}`

}